import { BehaviorSubject, Observable } from 'rxjs';

import { InstrumentationData } from '../../shared/instrumentation/instrumentation-data.model';
import { GraphDriveItem } from '../../shared/models/onedrive-graph.model';
import { Performance } from '../../shared/services/performance.service';
import { PromiseCancellationToken } from '../../shared/utilities/promise-cancellation-token.utility';

export class UploadSessionBase {

    private readonly startTime: number;
    private readonly progress: BehaviorSubject<UploadSessionProgress>;
    private readonly fileSize: number;
    private lastLoaded: number;

    constructor(
        public sessionId: string,
        public file: File,
        public uploadSource: UploadSource,
        public cancellationToken: PromiseCancellationToken = null,
        public bytesUploaded: number = 0,
        public state: UploadSessionState = UploadSessionState.Started,
        public error: UploadError = null,
        public sessionUrl: string = null
    ) {
        this.startTime = Performance.now();
        this.fileSize = file.size;
        this.lastLoaded = bytesUploaded;
        this.progress = new BehaviorSubject<UploadSessionProgress>(this.progressState());
        this.cancellationToken = cancellationToken || new PromiseCancellationToken();
    }

    public finished(): boolean {
        return this.state === UploadSessionState.Completed || this.state === UploadSessionState.Cancelled || this.state === UploadSessionState.Failed;
    }

    public updateProgress(bytesLoaded: number): void {
        if (typeof (bytesLoaded) !== 'undefined') {
            // Ensure the progress never moves backward or past 100%.
            this.lastLoaded = Math.min(Math.max(this.lastLoaded, bytesLoaded), this.fileSize);
            this.progress.next(this.progressState());
        }
    }

    public getProgress(): Observable<UploadSessionProgress> {
        return this.progress.asObservable();
    }

    public get duration(): number {
        return Performance.now() - this.startTime;
    }

    private progressState(): UploadSessionProgress {
        return {
            percentage: Math.floor(100 * this.lastLoaded / this.fileSize),
            loaded: this.lastLoaded,
            total: this.fileSize
        };
    }
}

export class UploadSession<TResult> extends UploadSessionBase {
    public result: TResult;
}

export interface UploadSessionProgress {
    percentage: number;
    loaded: number;
    total: number;
}

export enum UploadSessionState {
    Unknown,
    Started,
    Completed,
    Cancelled,
    Failed
}

export interface UploadError {
    type: UploadErrorType;
    title: string;
    message: string;
    recourseAction?: UploadErrorRecourseAction;
}

export enum UploadErrorType {
    Unknown,
    FileTooLarge,
    OutOfSpace,
    InvalidFileType,
    BadConnection,
    Timeout,
    Auth,
    MultipleFiles
}

export interface UploadErrorRecourseAction {
    text: string;
    ariaLabel: string;
    action: () => void;
    clickData: InstrumentationData;
}

export interface ResumableUploadState {
    nextExpectedRanges?: Array<string>;
    sessionUrl?: string;
    waitDuration?: number;
    clientAttemptsRemaining?: number;
    serverAttemptsRemaining?: number;
    item?: GraphDriveItem;
    error?: any;
    speed?: number;
}

export interface UploadService<TResult> {
    upload(file: File, sessionId: string, uploadSource: UploadSource): Observable<UploadSession<TResult>>;
}

export enum UploadSource {
    Button,
    DragAndDrop,
    CreateMenu
}
